/**
 * The editor's side of the design-system sandbox: a hidden iframe with an
 * OPAQUE origin (sandbox="allow-scripts", never allow-same-origin) and a CSP
 * that allows only the runtime script and blob: modules — no fetch, no
 * storage, no cookies, no navigation. Uploaded design-system code runs there
 * and nowhere else; the editor gets back HTML, which it sanitizes anyway.
 */
import DOMPurify from "dompurify";

import {
  isSandboxResponse,
  READY_TYPE,
  type DsWidgetDescription,
  type LoadResult,
  type SandboxRequest,
  type SandboxResponse,
} from "./protocol.js";
import type { ConformanceReport } from "./conformance.js";

export const RUNTIME_PATH = "/ds-sandbox-runtime.js";

type Payload<R> = R extends SandboxRequest ? Omit<R, "id"> : never;

/** The sandbox document: nothing but the runtime, under a no-network CSP. */
export function sandboxDocument(hostOrigin: string): string {
  const runtime = `${hostOrigin}${RUNTIME_PATH}`;
  const csp = [
    "default-src 'none'",
    `script-src ${runtime} blob:`,
    "style-src 'unsafe-inline'",
    "img-src data:",
    "font-src data:",
    "connect-src 'none'",
    "form-action 'none'",
    "base-uri 'none'",
  ].join("; ");
  return (
    `<!doctype html><html><head><meta charset="utf-8">` +
    `<meta http-equiv="Content-Security-Policy" content="${csp}">` +
    `</head><body><script src="${runtime}" data-host-origin="${hostOrigin}"></script>` +
    `</body></html>`
  );
}

/**
 * Sanitize HTML that came out of the sandbox before it goes anywhere near the
 * editor or a published page: no scripts, no event handlers, no javascript:
 * URLs, no forms (the page places its own), no embedding or metadata tags.
 */
export function sanitizeDsHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    FORBID_TAGS: [
      "form",
      "script",
      "iframe",
      "object",
      "embed",
      "link",
      "meta",
      "base",
      "style",
    ],
    ALLOW_DATA_ATTR: false,
  });
}

export interface DsSandbox {
  load(code: string): Promise<LoadResult>;
  /** Sanitized HTML per widget key. */
  render(
    widgets: ReadonlyArray<DsWidgetDescription>,
  ): Promise<Record<string, string>>;
  /** Semantic conformance of the loaded design system (see conformance). */
  conform(): Promise<ConformanceReport>;
  dispose(): void;
}

export async function createDsSandbox(
  opts: { readonly timeoutMs?: number; readonly parent?: HTMLElement } = {},
): Promise<DsSandbox> {
  const timeoutMs = opts.timeoutMs ?? 20_000;
  const iframe = document.createElement("iframe");
  iframe.setAttribute("sandbox", "allow-scripts");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.display = "none";
  iframe.srcdoc = sandboxDocument(location.origin);

  const pending = new Map<number, (r: SandboxResponse) => void>();
  let ready!: () => void;
  const readyP = new Promise<void>((r) => (ready = r));
  // An opaque-origin document reports its origin as "null".
  const onMessage = (e: MessageEvent) => {
    if (e.source !== iframe.contentWindow || e.origin !== "null") return;
    const data = e.data as unknown;
    if (
      typeof data === "object" &&
      data !== null &&
      (data as { type?: unknown }).type === READY_TYPE
    )
      return ready();
    if (!isSandboxResponse(data)) return;
    pending.get(data.id)?.(data);
    pending.delete(data.id);
  };
  window.addEventListener("message", onMessage);
  (opts.parent ?? document.body).appendChild(iframe);

  const withTimeout = <T>(p: Promise<T>, what: string) =>
    Promise.race([
      p,
      new Promise<never>((_, rej) =>
        setTimeout(
          () => rej(new Error(`the design-system sandbox timed out (${what})`)),
          timeoutMs,
        ),
      ),
    ]);

  await withTimeout(readyP, "start");

  let nextId = 1;
  const request = async (payload: Payload<SandboxRequest>) => {
    const id = nextId++;
    const res = await withTimeout(
      new Promise<SandboxResponse>((resolve) => {
        pending.set(id, resolve);
        // An opaque origin can only be addressed as "*"; the reply is
        // checked by source and origin above.
        iframe.contentWindow!.postMessage({ ...payload, id }, "*");
      }),
      payload.type,
    );
    if (!res.ok) throw new Error(res.error);
    return res.result;
  };

  return {
    async load(code) {
      const r = (await request({ type: "load", code })) as LoadResult;
      return { id: String(r.id), name: String(r.name) };
    },
    async render(widgets) {
      const r = (await request({ type: "render", widgets })) as Record<
        string,
        unknown
      >;
      const out: Record<string, string> = {};
      for (const w of widgets) {
        const html = r[w.key];
        out[w.key] = typeof html === "string" ? sanitizeDsHtml(html) : "";
      }
      return out;
    },
    async conform() {
      const r = (await request({ type: "conform" })) as {
        ok?: unknown;
        failures?: unknown;
        featureFailures?: unknown;
      };
      const failures = Array.isArray(r.failures)
        ? r.failures.filter((f): f is string => typeof f === "string")
        : ["the sandbox returned no conformance report"];
      // The report crossed from untrusted code: keep only well-formed rows.
      const featureFailures = Array.isArray(r.featureFailures)
        ? r.featureFailures.flatMap((f: unknown) => {
            const row = f as { feature?: unknown; message?: unknown };
            const feature = row.feature;
            return feature === "icon" && typeof row.message === "string"
              ? [{ feature, message: row.message } as const]
              : [];
          })
        : [];
      return {
        ok:
          r.ok === true &&
          failures.length === 0 &&
          featureFailures.length === 0,
        failures,
        featureFailures,
      };
    },
    dispose() {
      window.removeEventListener("message", onMessage);
      iframe.remove();
      pending.clear();
    },
  };
}
